// File: src/components/layouts/navigation/Navbar/Navbar.tsx

import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { Menu, X } from "lucide-react";
import { Button, Typography } from "../../../general";
import { Flex } from "../../wrappers/Flex";
import DefaultLogo from "../../../../assets/logo.svg";
import DefaultBrand from "../../../../assets/brand.svg";
import { ScrollOptions } from "hooks";

interface User {
  name: string;
  avatar?: string;
}

interface NavbarProps {
  logo?: string;
  brand?: string;
  showBrand?: boolean;
  showLogo?: boolean;
  brandText?: string;
  compactBrand?: boolean;
  // links?: { label: string; path: string; external?: boolean }[];
  links?: { label: string; path: string; external?: boolean; active?: boolean }[];
  scrollToSection?: (
    idOrElement: string | HTMLElement,
    options?: ScrollOptions
  ) => void;
  scrollOptions?: ScrollOptions| undefined;
  backgroundColor?: string; // ✅ Support for transparent/custom bg

  scrollOffset?: number | undefined;
  rightActions?: { icon: React.ReactNode; onClick: () => void }[];
  onMenuToggle?: () => void;
  ctaLabel?: string;
  ctaOnClick?: () => void;
  sticky?: boolean;
  showSearch?: boolean;
  overlayMode?: boolean;
  user?: User | undefined | null;
  onLogin?: () => void;
  onLogout?: () => void;
  onSignUp?: () => void;
  userSection?: React.ReactNode;

}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledNavbar = styled.nav<{ sticky?: boolean, backgroundColor: any }>`
  width: 100%;
  height: 64px;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme , backgroundColor}) => backgroundColor ? backgroundColor : theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  box-shadow: ${({ theme }) => theme.elevations.md};
  position: ${({ sticky }) => (sticky ? "sticky" : "relative")};
  top: 0;
  z-index: 1000;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const BrandSection = styled(Flex)<{ compact?: boolean }>`
  flex: 1;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  min-width: 0;

  .brand-logo {
    height: ${({ compact }) => (compact ? "36px" : "48px")};
  }

  .brand-image{
    display: block;
    height: ${({ compact }) => (compact ? "40px" : "60px")};

     @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      display: none;
    }
  }
  .brand-text {
    display: block;

    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      display: none;
    }
  }
`;

const NavLinks = styled(Flex)`
  gap: ${({ theme }) => theme.spacing.lg};
  align-items: flex-start;
  margin-left: ${({ theme }) => theme.spacing.lg};

  a {
    color: ${({ theme }) => theme.colors.textPrimary};
    text-decoration: none;
    font-weight: ${({ theme }) => theme.fonts.fontWeightMedium};
    transition: color 0.2s;

    &.active {
      color: ${({ theme }) => theme.colors.primary};
      border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
    }
      
    &:hover {
      color: ${({ theme }) => theme.colors.primaryHover};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const RightSection = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-shrink: 0;

  input[type="text"] {
    display: none;
    border: 1px solid ${({ theme }) => theme.colors.border};
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.radii.md};
    font-size: ${({ theme }) => theme.typography.body.fontSize};

    @media (min-width: ${({ theme }) => theme.breakpoints.laptop}) {
      display: block;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileMenuToggle = styled(Button)`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
  }
`;

const OverlayMenu = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  z-index: 3000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
  transform: ${({ open }) => (open ? "translateY(0)" : "translateY(-8px)")};
  transition: opacity 0.3s ease, transform 0.3s ease;

  .close-button {
    position: absolute;
    top: ${({ theme }) => theme.spacing.md};
    right: ${({ theme }) => theme.spacing.md};
  }

  .overlay-links {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg};
    text-align: center;

    a {
      color: ${({ theme }) => theme.colors.textPrimary};
      text-decoration: none;
      font-size: 1.2rem;

      &:hover {
        color: ${({ theme }) => theme.colors.primary};
      }
    }
  }
`;

export const Navbar: React.FC<NavbarProps> = ({
  logo,
  brand,
  showBrand = true,
  showLogo = true,
  brandText = "Brand",
  compactBrand = false,
  links = [],
  rightActions = [],
  onMenuToggle,
  ctaLabel,
  ctaOnClick,
  sticky = false,
  showSearch = false,
  overlayMode = false,
  scrollToSection,
  scrollOffset,
  scrollOptions,
  backgroundColor,
  user,
  onLogout,
  onSignUp,
  onLogin,
  userSection
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <StyledNavbar sticky={sticky} backgroundColor={backgroundColor}>
        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
          <BrandSection compact={compactBrand}>
            {showLogo && (
              <img src={logo || DefaultLogo} alt="Logo" className="brand-logo" />
              
            )}

            {showBrand && (
              <img src={brand || DefaultBrand} alt="Brand" className="brand-image" />
            )}
            {!showBrand && !showLogo && (
              <Typography variant="title" weight="bold" className="brand-text">
                {brandText}
              </Typography>
            )}
          </BrandSection>

          <Flex align="center" gap="lg" style={{ flex: '0 0 auto' }}>
            <NavLinks>
            {links.map((link, index) => {
              const isAnchorLink = link.path.startsWith("#");
              const sectionId = link.path.replace("#", "");

              return (
                <a
                  key={index}
                  href={link.path}
                  className={link.active ? "active" : ""}
                  onClick={(e) => {
                    if (isAnchorLink && scrollToSection) {
                      e.preventDefault();
                      scrollToSection(sectionId, scrollOptions ? scrollOptions : { offset: scrollOffset ? scrollOffset : -40, behavior: "smooth", duration: 1500 });
                    }
                  }}
                  target={link.external ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              );
            })}
            </NavLinks>

            <RightSection>
              {showSearch && <input type="text" placeholder="Search..." />}

              {rightActions.map((action, idx) => (
                <Button key={idx} variant="outline" size="compact" tight onClick={action.onClick}>
                  {action.icon}
                </Button>
              ))}

              {userSection ? (
                  userSection
                ) : user ? (
                <>
                  <Typography as="span" variant="caption" colorScheme="textSecondary">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginRight: 8,
                        }}
                      />
                    ) : (
                      `Hi, ${user.name}`
                    )}
                  </Typography>
                  <Button variant="ghost" size="compact" onClick={onLogout}>
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="compact" onClick={onLogin}>
                    Login
                  </Button>
                  <Button variant="primary" size="compact" onClick={onSignUp}>
                    Sign Up
                  </Button>
                </>
              )}
            </RightSection>

            <MobileMenuToggle
              variant="ghost"
              size="compact"
              onClick={() => {
                setMenuOpen(!menuOpen);
                onMenuToggle?.();
              }}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </MobileMenuToggle>
          </Flex>
        </Flex>
      </StyledNavbar>

      {overlayMode && (
        <OverlayMenu open={menuOpen}>
          <Button className="close-button" variant="ghost"  size="compact" onClick={() => setMenuOpen(false)}>
            <X size={24} />
          </Button>
          <div className="overlay-links">
            {links.map((link, index) => (
              <a key={index} href={link.path} target={link.external ? "_blank" : "_self"}>
                {link.label}
              </a>
            ))}
          </div>
        </OverlayMenu>
      )}
    </>
  );
};
